import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardBody, CardHeader, Modal, Row, Col, Alert, Spinner, Input, Button } from 'reactstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import api from '../helpers/api';
import FeatherIcon from 'feather-icons-react';
import * as XLSX from 'xlsx';
import FileModal from '../components/Common/importExportModal';
import LocationModal from '../components/LocationModal';
import ParentLocationSelector from '../components/ParentLocationSelector';
import { formatCreatedDate } from '../constants/utils';
import TicketDetailsModal from '../components/TicketDetailsModal';
import UserModal from '../components/UserModal';
import { MdSort } from "react-icons/md";
import PermissionGuard from '../components/Common/PermissionGuard';
import usePermission from '../Hooks/usePermission';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import {
    buildCrossLocaleHeaderMap,
    detectHeaderRow,
    normalizeHeader,
    normalizeName,
    readRowsFromHeader,
    readWorkbook,
    spreadsheetRowNumber,
} from '../utils/importSheet';
import useImportJob from '../Hooks/useImportJob';
import ImportResumeBanner from '../components/Common/ImportResumeBanner';

const Location = () => {
    const { t } = useTranslation();
    document.title = `${t("shared.locations", "Locations")} | ArcMedix`;
    const currentUser = JSON.parse(localStorage.getItem('authUser'));
    const { isAdmin } = usePermission();

    // Check if user can delete a location (must be owner or admin)
    const canDeleteLocation = (location) => {
        if (isAdmin) return true;
        // Use createdBy (Long) from BaseDTO for ownership check
        const createdById = location?.createdBy;
        const userId = currentUser?.id;
        // If createdBy not present (backend not updated), allow based on permission only
        if (createdById === undefined || createdById === null) return true;
        return createdById === userId;
    };
    const [locations, setLocations] = useState([]);
    const [modal_large, setmodal_large] = useState(false);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [errors, setErrors] = useState({});
    const [apiError, setApiError] = useState('');
    const [deleteModal, setDeleteModal] = useState(false);
    const [sortModal, setSortModal] = useState(false);
    const [count, setCount] = useState(10);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [fileModal, setFileModal] = useState(false);
    // Bulk import runs as a server-side job: upload → preview → confirm.
    const locationImport = useImportJob({
        entityType: 'LOCATION',
        onCompleted: () => reloadLocations(),
    });
    const startImportJob = locationImport.start;
    const [enumData, setEnumData] = useState([]);
    
    
    // Sort criteria state
    const [sortCriteria, setSortCriteria] = useState({
        field: 'locationName',
        direction: 'asc'
    });
    
    const [detailsModal, setDetailsModal] = useState({
        isOpen: false,
        title: '',
        mainData: null,
        relatedData: [],
        loading: false,
        locationData: null,
        initialActiveTab: null,
    });
    const [searchParams, setSearchParams] = useSearchParams();
    const [ticketModal, setTicketModal] = useState({
        isOpen: false,
        ticketData: null,
        loading: false
    });
    const [reportedByModal, setReportedByModal] = useState({
        isOpen: false,
        userData: null,
        loading: false
    });
    
      
    const user = JSON.parse(localStorage.getItem('authUser'));
    // Build a lightweight org object from the active context so this page
    // mirrors whichever org the user has currently switched into.
    const activeOrgId = user?.activeOrganizationId ?? null;
    const activeOrgName = user?.activeContextName
      || (user?.memberships || []).find((m) => m.organizationId === activeOrgId)?.organizationName
      || null;
    const organization = activeOrgId ? { id: activeOrgId, name: activeOrgName } : null;

    // Sorting function
    const sortLocations = useCallback((data) => {
        if (!data || data.length === 0) return data;
        
        return [...data].sort((a, b) => {
            let valueA, valueB;
            
            switch(sortCriteria.field) {
                case 'createdAt':
                case 'updatedAt':
                    valueA = new Date(a[sortCriteria.field] || 0);
                    valueB = new Date(b[sortCriteria.field] || 0);
                    break;
                case 'locationName':
                    valueA = a.locationName?.toLowerCase() || '';
                    valueB = b.locationName?.toLowerCase() || '';
                    break;
                default:
                    valueA = a[sortCriteria.field] || '';
                    valueB = b[sortCriteria.field] || '';
                    if (typeof valueA === 'string') valueA = valueA.toLowerCase();
                    if (typeof valueB === 'string') valueB = valueB.toLowerCase();
            }
            
            if (sortCriteria.direction === 'asc') {
                return valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
            } else {
                return valueA < valueB ? 1 : valueA > valueB ? -1 : 0;
            }
        });
    }, [sortCriteria]);

    // Flatten the hierarchical location tree returned by hierarchy=true into rows.
    // Each row keeps its `subLocations` array so the "N Sub-Locations" chip count works.
    // Mirrors flattenAssetsWithCount in Page/Assets.jsx. Deduplicates by id because a search
    // hit can return both a parent and one of its descendants as top-level matches, with the
    // descendant nested under the parent's subLocations as well.
    const flattenLocationsWithCount = (tree, result = [], seen = new Set()) => {
        tree?.forEach((loc) => {
            if (loc?.id != null && !seen.has(loc.id)) {
                seen.add(loc.id);
                result.push(loc);
            }
            if (loc.subLocations?.length > 0) {
                flattenLocationsWithCount(loc.subLocations, result, seen);
            }
        });
        return result;
    };

    const fetchLocations = useCallback(async () => {
        setLoading(true);
        try {
            const {data} = await api.get(
                `organization-location/get-by-organization-id?organizationId=${organization?.id}${
                    searchTerm ? `&search=${searchTerm}` : ''
                }`
            );

            const tree = data.response[0]?.locations || [];
            const flat = flattenLocationsWithCount(tree);

            setLocations(sortLocations(flat));
            setHasMore(false);
        } catch (error) {
            console.error('Error fetching locations:', error);
            setHasMore(false);
        } finally {
            setLoading(false);
        }
    }, [organization?.id, searchTerm, sortLocations]);

    // Re-sort when sort criteria changes
    useEffect(() => {
        if (locations.length > 0) {
            setLocations(prevLocations => sortLocations(prevLocations));
        }
    }, [sortCriteria, sortLocations]);



    const onStatusChange = async (data, value) => {
        try {
          const payload = { id: data?.id, status: value };
          // Set resolvedAt timestamp when marking as RESOLVED
          if (value === 'RESOLVED') {
            payload.resolvedAt = new Date().toISOString();
          }
          const response = await api.put('asset-issues/update', payload);
        } catch (error) {
          console.error('Failed to update status:', error);
        }
    };

    const handleTicketClick = async (ticketNumber) => {
        setTicketModal(prev => ({
          ...prev,
          isOpen: true,
          ticketData: null,
          loading: true
        }));

        try {
          const { data } = await api.get(`asset-issues/organization/filtered`, {
            params: { 
              ticket: ticketNumber
            }
          });
          
          setTicketModal(prev => ({
            ...prev,
            ticketData: data.response?.[0] || null,
            loading: false
          }));
        } catch (error) {
          console.error('Error fetching ticket details:', error);
          setTicketModal(prev => ({ ...prev, loading: false }));
        }
    };

    const fetchEnums = async () => {
        try {
          const { data } = await api.get("dynamic-enum/get-all");
          setEnumData(data.response || []);
        } catch (error) {
          console.error("Error fetching enums:", error);
        }
    };

    const loadMoreLocations = async () => {
        if (!hasMore) return;
        setCount(prevCount => prevCount + 10);
    };

    const reloadLocations = () => {
        setCount(10);
        setLocations([]);
        setHasMore(true);
    };

    // Column dictionary for the location importer. Mirrors the Assets page: one entry per column,
    // carrying both the parser mapping and everything the downloadable template needs, so headers
    // and their descriptions cannot drift apart.
    const tplDesc = (k) => t(`business.locations.import.template.descriptions.${k}`);
    const tplFmt = (k) => t(`business.locations.import.template.formats.${k}`);
    const tplHeader = (k) => t(`business.locations.import.template.headers.${k}`, k);
    const LOCATION_FIELD_MAPPING = {
        // Blank creates a location; a value updates the one it came from. Without this an edited
        // export could only ever add duplicates — which is what re-importing used to do.
        'Arcmedix ID': {
            field: 'arcmedixId',
            template: { headerI18nKey: 'arcmedixId', headerLabel: tplHeader('arcmedixId'), description: tplDesc('arcmedixId'), formatHint: tplFmt('arcmedixId'), example1: '', example2: '' },
        },
        'Name': {
            field: 'locationName', required: true,
            template: { headerI18nKey: 'name', headerLabel: tplHeader('name'), description: tplDesc('name'), formatHint: tplFmt('name'), example1: 'Building A', example2: 'Floor 1' },
        },
        'Parent Location': {
            field: 'parentLocationName',
            template: { headerI18nKey: 'parentLocation', headerLabel: tplHeader('parentLocation'), description: tplDesc('parentLocation'), formatHint: tplFmt('parentLocation'), example1: '', example2: 'Building A' },
        },
        'Parent Location ID': {
            field: 'parentLocationId',
            template: { headerI18nKey: 'parentLocationId', headerLabel: tplHeader('parentLocationId'), description: tplDesc('parentLocationId'), formatHint: tplFmt('parentLocationId'), example1: '', example2: '' },
        },
        'Address': {
            field: 'locationAddress',
            template: { headerI18nKey: 'address', headerLabel: tplHeader('address'), description: tplDesc('address'), formatHint: tplFmt('address'), example1: '12 Rue Centrale', example2: '' },
        },
    };

    // Issues come back keyed by canonical field name; show the user the header they actually saw.
    const importColumnLabels = Object.entries(LOCATION_FIELD_MAPPING).reduce((acc, [header, cfg]) => {
        acc[cfg.field] = cfg.template?.headerLabel || header;
        return acc;
    }, {});

    const handleImport = async (file) => {
        try {
            setLoading(true);
            setApiError('');

            const { worksheet, rawRows, originRow } = await readWorkbook(file);
            const headerMap = buildCrossLocaleHeaderMap({
                mapping: LOCATION_FIELD_MAPPING,
                i18n,
                labelLookup: (bundle, key) => bundle?.business?.locations?.import?.template?.headers?.[key],
            });

            const headerOffset = detectHeaderRow({ rawRows, headerMap, requiredKey: 'Name' });
            if (headerOffset === -1) {
                setApiError(t('business.locations.import.header_not_found',
                    'Could not find the column header row. Make sure your sheet has a header row that includes a "Name" column.'));
                return;
            }

            const jsonData = readRowsFromHeader(worksheet, originRow, headerOffset);
            const rows = jsonData
                .map((item, rowIndex) => {
                    const values = {};
                    Object.keys(item).forEach((header) => {
                        const mapKey = headerMap[normalizeHeader(header)];
                        if (!mapKey) return;
                        const value = item[header];
                        if (value === undefined || value === null || value === '') return;
                        const field = LOCATION_FIELD_MAPPING[mapKey].field;
                        // Names are collapsed and trimmed here: the column collation is NO PAD, so a
                        // trailing space from a spreadsheet would make every later lookup miss.
                        values[field] = (field === 'locationName' || field === 'parentLocationName')
                            ? normalizeName(value)
                            : String(value).trim();
                    });
                    return {
                        rowNumber: spreadsheetRowNumber(rowIndex, originRow, headerOffset),
                        values,
                        customFields: {},
                    };
                })
                .filter((row) => Object.keys(row.values).length > 0);

            if (rows.length === 0) {
                setApiError(t('business.locations.no_valid_data', 'No valid location data found in the file'));
                return;
            }

            const created = await startImportJob({ fileName: file.name, rows });
            if (created) {
                setFileModal(true);
            }
        } catch (error) {
            setApiError(error?.message || t("shared.error_processing_file", "Error processing the file"));
            console.error('Import error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFileModal(false);
        setApiError('');
        // A finished import must not linger in state: reopening the dialog would show the previous
        // run's result screen instead of letting the user upload the next file.
        if (locationImport.job && !['VALIDATING', 'READY', 'COMMITTING'].includes(locationImport.job.status)) {
            locationImport.reset();
        }
    };

    /**
     * Builds "Site / Building / Floor" for a location by walking up the flattened list. Read-only
     * on import, but it is what makes an exported tree readable — a bare name in a 2,000-row sheet
     * tells the user nothing about which branch it belongs to.
     */
    const buildFullPath = (location, byId) => {
        const parts = [];
        const seen = new Set();
        let current = location;
        while (current && !seen.has(current.id)) {
            seen.add(current.id);
            parts.unshift(current.locationName || '');
            const parentId = current.parentLocationId ?? current.parentLocation?.id ?? null;
            current = parentId ? byId[parentId] : null;
        }
        return parts.join(' / ');
    };

    const handleExport = () => {
        if (!locations || locations.length === 0) return;

        const byId = {};
        locations.forEach((loc) => { if (loc?.id != null) byId[loc.id] = loc; });

        const dataToExport = locations.map((location) => {
            const parentId = location?.parentLocationId ?? location?.parentLocation?.id ?? '';
            return {
                // The id first: without it this file can only ever create duplicates when it comes
                // back in, which is exactly what re-importing a location export used to do.
                'Arcmedix ID': location?.id ?? '',
                'Name': location?.locationName || '',
                'Parent Location': location?.parentLocation?.locationName
                    || (parentId && byId[parentId] ? byId[parentId].locationName : ''),
                'Parent Location ID': parentId,
                'Address': location?.locationAddress || '',
                // Export-only columns; ignored on import.
                'Full Path': buildFullPath(location, byId),
                'Created At': formatCreatedDate(location?.createdAt),
                'Updated At': formatCreatedDate(location?.updatedAt),
                'Created By': location?.createdByUser?.name || '',
                'Sublocations Count': location?.subLocations?.length || 0,
            };
        });

        const ws = XLSX.utils.json_to_sheet(dataToExport);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Locations');
        XLSX.writeFile(wb, `locations_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    /**
     * Downloads a blank template. Locations never had one, so people guessed at the columns and
     * the importer only ever understood two of them.
     */
    const handleDownloadTemplate = async () => {
        const ExcelJS = (await import('exceljs')).default;
        const columns = Object.entries(LOCATION_FIELD_MAPPING);
        const pick = (key) => columns.map(([, cfg]) => cfg.template?.[key] ?? '');

        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet(t('shared.locations', 'Locations'));

        sheet.addRow([t('business.locations.import.template.title', 'Location import template')]);
        sheet.addRow([t('business.locations.import.template.instructions',
            'Leave "Arcmedix ID" blank to create a location. Keep the exported value to update it. Blank cells leave existing values unchanged.')]);
        const headerRow = sheet.addRow(pick('headerLabel'));
        headerRow.font = { bold: true };
        sheet.addRow(pick('example1'));
        sheet.addRow(pick('example2'));

        columns.forEach(([, cfg], index) => {
            const cell = headerRow.getCell(index + 1);
            const note = [cfg.template?.description, cfg.template?.formatHint].filter(Boolean).join('\n');
            if (note) cell.note = note;
            sheet.getColumn(index + 1).width = Math.max(18, String(cfg.template?.headerLabel || '').length + 4);
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'location_import_template.xlsx';
        link.click();
        URL.revokeObjectURL(url);
    };

    // A commit keeps running server-side even if the tab is closed, and a preview waits for a
    // decision. On mount, pick either back up rather than losing it silently.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const resumable = await locationImport.findResumable();
            if (!cancelled && resumable?.id) {
                await locationImport.attach(resumable.id);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organization?.id]);

    const handleSearch = (e) => {
        const term = e.target.value;
        setSearchTerm(term);
        setCount(10);
        setLocations([]);
        setHasMore(true);
    };

    const tog_large = (entry = null) => {
        setmodal_large(!modal_large);
        if (entry) {
            // Track the parent at the moment of opening so handleUpdate can tell apart
            // "user did nothing" (keep parent) from "user cleared parent" (detach to root).
            const original = entry.parentLocationId ?? entry.parentLocation?.id ?? null;
            setSelectedEntry({ ...entry, originalParentLocationId: original });
        } else {
            setSelectedEntry({ locationName: '', locationAddress: '' });
        }
        setErrors({});
        setApiError('');
    };

    // Lazy-fetch the parent object on edit when it isn't already present on the entry,
    // mirrors the AssetModal/Assets.jsx pattern (entry.parent absent → fetch get-by-id).
    const openEditWithParentFetch = useCallback(async (entry) => {
        if (!entry?.id) {
            tog_large(entry);
            return;
        }
        if (entry.parentLocation || !entry.parentLocationId) {
            tog_large(entry);
            return;
        }
        try {
            const { data } = await api.get(`organization-location/get-by-id?id=${entry.id}`);
            tog_large(data?.response || entry);
        } catch (err) {
            console.error('Failed to fetch full location for edit:', err);
            tog_large(entry);
        }
    }, []);

    const handleModalClose = () => {
        setmodal_large(false);
        setSelectedEntry(null);
    };

    const handleLocationClick = async (location, initialActiveTab = null) => {
        const enhancedLocationData = {
            ...location,
            organization: location?.organization || organization,
        };

        setDetailsModal({
            isOpen: true,
            title: t("shared.location_details", 'Location Details'),
            mainData: location?.locationName,
            relatedData: [],
            loading: true,
            locationData: enhancedLocationData,
            initialActiveTab,
        });

        try {
            const { data: response } = await api.get('asset-issues/organization/filtered', {
                params: { organizationLocation: location?.locationName, organizationId: organization?.id },
            });
            setDetailsModal(prev => ({
                ...prev,
                relatedData: response.response || [],
                loading: false,
            }));
        } catch (error) {
            console.error('Error fetching location work orders:', error);
            setDetailsModal(prev => ({ ...prev, loading: false }));
        }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();

        const newErrors = {};
        if (!selectedEntry?.locationName?.trim()) newErrors.locationName = t("business.locations.name_required", 'Location name is required');

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        try {
            setApiError('');

            const newParentId = selectedEntry?.parentLocation?.id ?? selectedEntry?.parentLocationId ?? null;

            if (selectedEntry?.id) {
                const previousParentId = selectedEntry?.originalParentLocationId ?? null;
                const payload = {
                    id: selectedEntry.id,
                    locationName: selectedEntry.locationName,
                    locationAddress: selectedEntry.locationAddress || '',
                    organizationId: organization?.id,
                };
                if (newParentId !== null) {
                    payload.parentLocationId = newParentId;
                } else if (previousParentId !== null) {
                    // Detach to root: ask the backend to clear the parentLocationId.
                    payload.clearFields = ['PARENT_LOCATION_ID'];
                }
                await api.put(`organization-location/update`, payload);
            } else {
                const payload = {
                    locationName: selectedEntry.locationName,
                    locationAddress: selectedEntry.locationAddress || '',
                    organizationId: organization?.id,
                };
                if (newParentId !== null) payload.parentLocationId = newParentId;
                await api.post('organization-location/add', [payload]);
            }

            fetchLocations();

            // Update details modal if it's open and showing the same location
            if (detailsModal.isOpen &&
                detailsModal.locationData?.id === selectedEntry?.id) {
                const updatedLocationData = {
                    ...detailsModal.locationData,
                    locationName: selectedEntry.locationName,
                    locationAddress: selectedEntry.locationAddress
                };

                setDetailsModal(prev => ({
                    ...prev,
                    locationData: updatedLocationData,
                    mainData: selectedEntry.locationName,
                    title: selectedEntry.locationName
                }));
            }

            handleModalClose();
        } catch (error) {
            setApiError(error?.response?.data?.message || t("shared.something_went_wrong", "Something went wrong!"));
        }
    };

    const handleDelete = async () => {
        try {
            await api.delete(`organization-location/delete?id=${selectedEntry?.id}`);
            fetchLocations();
            setDeleteModal(false);
        } catch (error) {
            console.error('Error deleting location:', error);
            setApiError(t("business.locations.failed_delete", 'Failed to delete location'));
        }
    };

    useEffect(() => {
        fetchEnums()
    }, []);

    const handleReportedByClick = async (userId, fromDate = null, toDate = null) => {
      if (!userId) return;

      setReportedByModal(prev => ({
        ...prev,
        isOpen: true,
        userData: null,
        loading: true
      }));

      try {
        const { data: userData } = await api.get(`user/get-all?id=${userId}`);

        const params = { 
          reportedBy: userId,
          organizationId: organization?.id,
          count: 5
        };

        if (fromDate) params.fromDate = fromDate;
        if (toDate) params.toDate = toDate;

        const { data: issuesData } = await api.get(`asset-issues/organization/filtered`, { params });

        setReportedByModal(prev => ({
          ...prev,
          userData: {
            ...userData.response[0],
            reportedIssues: issuesData.response || []
          },
          loading: false
        }));
      } catch (error) {
        console.error('Error fetching reported by details:', error);
        setReportedByModal(prev => ({ ...prev, loading: false }));
      }
    };

    useEffect(() => {
        fetchLocations();
    }, [fetchLocations]);

    // Deep-link: /locations?createSubLocationOf=<id> opens the create form with that parent pre-selected.
    // Mirrors the asset deep-link pattern. The query param is stripped after the form opens so a refresh
    // doesn't re-trigger the same flow.
    useEffect(() => {
        const parentIdParam = searchParams.get('createSubLocationOf');
        if (!parentIdParam) return;
        let cancelled = false;
        const openCreateUnderParent = async () => {
            try {
                const { data } = await api.get(`organization-location/get-by-id?id=${parentIdParam}`);
                if (cancelled) return;
                const parent = data?.response;
                if (parent) {
                    setSelectedEntry({
                        locationName: '',
                        locationAddress: '',
                        parentLocation: parent,
                        parentLocationId: parent.id,
                    });
                    setErrors({});
                    setApiError('');
                    setmodal_large(true);
                }
            } catch (err) {
                console.error('Failed to resolve createSubLocationOf parent:', err);
            } finally {
                if (!cancelled) {
                    const next = new URLSearchParams(searchParams);
                    next.delete('createSubLocationOf');
                    setSearchParams(next, { replace: true });
                }
            }
        };
        openCreateUnderParent();
        return () => { cancelled = true; };
    }, [searchParams, setSearchParams]);

    // Use locations directly since they're already sorted
    const sortedLocations = locations;

    return (
        <div className='page-content mt-4'>
            <ImportResumeBanner job={locationImport.job} onResume={() => setFileModal(true)} />
            <div className="mb-4 d-flex align-items-center gap-3">
                <motion.div
                    className="mb-4 d-flex align-items-center gap-3"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    <PermissionGuard permission="location:create">
                        <button
                            type="button"
                            className="btn btn-soft-success waves-effect waves-light d-flex align-items-center gap-2 transition-all hover-shake"
                            onClick={() => tog_large()}
                        >
                            <FeatherIcon icon="plus" />
                            {t("business.locations.add_location", "Add Location")}
                        </button>
                    </PermissionGuard>
                    <div className="ms-auto" style={{ minWidth: '400px', flexGrow: 1, maxWidth: '500px' }}>
                        <Input
                            type="text"
                            placeholder={t("shared.search_locations", "Search locations...")}
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                    </div>
                </motion.div>
            </div>
            
            <Card>
                <CardHeader>
                    <div className="d-flex justify-content-between align-items-center">
                        <h3 className="card-title text-success mb-0">{t("shared.locations", "Locations")}</h3>
                        <div className="d-flex align-items-center gap-2">
                            <button
                                className="btn btn-soft-primary p-2"
                                onClick={() => setSortModal(true)}
                                title={t("shared.sort_locations", "Sort locations")}
                            >
                                <MdSort size={20} />
                            </button>
                            <button 
                                className="btn btn-soft-info p-2"
                                onClick={() => setFileModal(true)}
                                title={t("shared.import_export_locations", "Import/Export locations")}
                            >
                                <FeatherIcon icon="file-text" size={18} />
                            </button>
                        </div>
                    </div>
                </CardHeader>
                
                <CardBody>
                    <div className="table-responsive">
                        <table className="table table-hover mb-0">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th 
                                        onClick={() => {
                                            setSortCriteria(prev => ({
                                                field: 'locationName',
                                                direction: prev.field === 'locationName' && prev.direction === 'desc' ? 'asc' : 'desc'
                                            }));
                                        }}
                                        className="cursor-pointer"
                                        style={{ cursor: 'pointer' }}
                                    >
                                        {t("shared.name", "Name")}
                                        {sortCriteria.field === 'locationName' && (
                                            <FeatherIcon 
                                                icon={sortCriteria.direction === 'asc' ? 'arrow-up' : 'arrow-down'} 
                                                size={14} 
                                                className="ms-1" 
                                            />
                                        )}
                                    </th>
                                    <th>{t("shared.address", "Address")}</th>
                                    <th>{t("shared.created_by", "Created By")}</th>
                                    <th
                                        onClick={() => {
                                            setSortCriteria(prev => ({
                                                field: 'createdAt',
                                                direction: prev.field === 'createdAt' && prev.direction === 'desc' ? 'asc' : 'desc'
                                            }));
                                        }}
                                        className="cursor-pointer"
                                        style={{ cursor: 'pointer' }}
                                    >
                                        {t("shared.created_at", "Created at")}
                                        {sortCriteria.field === 'createdAt' && (
                                            <FeatherIcon
                                                icon={sortCriteria.direction === 'asc' ? 'arrow-up' : 'arrow-down'}
                                                size={14}
                                                className="ms-1"
                                            />
                                        )}
                                    </th>
                                    <th>{t("shared.delete", "Delete")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <AnimatePresence>
                                    {sortedLocations.map((location, ind) => (
                                        <motion.tr
                                            key={location?.id}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -20 }}
                                            transition={{ duration: 0.3 }}
                                        >
                                            <th scope="row">{ind+1}</th>
                                            <td>
                                                <span
                                                    className="text-primary cursor-pointer hover-underline"
                                                    onClick={() => handleLocationClick(location)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    {location?.locationName}
                                                </span>
                                                {location?.subLocations?.length > 0 && (
                                                    <div className="mt-1">
                                                        <span
                                                            role="button"
                                                            onClick={(e) => { e.stopPropagation(); handleLocationClick(location, 'sublocations'); }}
                                                            className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded-pill text-primary fw-medium"
                                                            style={{ background: 'rgba(13,110,253,0.08)', fontSize: '11px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                                            title={t("business.locations.view_sub_locations_tooltip", "View sub-locations")}
                                                        >
                                                            {t("business.locations.n_sub_locations", "{{count}} Sub-Locations", { count: location.subLocations.length })}
                                                            <FeatherIcon icon="chevron-right" size={12} />
                                                        </span>
                                                    </div>
                                                )}
                                            </td>
                                            <td>{location?.locationAddress}</td>
                                            <td>
                                                <span
                                                    className="text-primary cursor-pointer hover-underline"
                                                    onClick={() => handleReportedByClick(location?.createdByUser?.id)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    {location?.createdByUser?.name}
                                                </span>
                                            </td>
                                            <td>{formatCreatedDate(location?.createdAt)}</td>
                                            <td>
                                                {canDeleteLocation(location) && (
                                                    <PermissionGuard permission="location:delete">
                                                        <span className='text-danger' role='button' onClick={() => {
                                                            setSelectedEntry(location);
                                                            setDeleteModal(true);
                                                        }}>
                                                            <FeatherIcon icon="trash-2" />
                                                        </span>
                                                    </PermissionGuard>
                                                )}
                                            </td>
                                        </motion.tr>
                                    ))}
                                </AnimatePresence>
                            </tbody>
                        </table>
                    </div>

                    {loading && (
                        <div className="text-center py-3">
                            <Spinner color="primary" />
                        </div>
                    )}

                    <div className="text-center mt-3">
                        {hasMore && sortedLocations.length > 0 ? (
                            <Button
                                color="primary"
                                onClick={loadMoreLocations}
                                disabled={loading}
                            >
                                {loading ? t("shared.loading", 'Loading...') : t("shared.load", 'Load More')}
                            </Button>
                        ) : (
                            sortedLocations.length > 0 && (
                                <div className="text-muted">{t("business.locations.no_more_locations", "No more locations to load")}</div>
                            )
                        )}
                    </div>

                    {sortedLocations.length === 0 && !loading && (
                        <div className="text-center py-5 text-muted">
                            {searchTerm
                                ? t("business.locations.no_matches", "No locations match your search")
                                : t("business.locations.no_locations", "No locations found")}
                        </div>
                    )}
                </CardBody>
            </Card>


            {/* Sort Modal */}
            <Modal
                isOpen={sortModal}
                toggle={() => setSortModal(false)}
                centered
                className="fade-scale-modal"
                backdropClassName="modal-backdrop-blur"
            >
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.2 }}
                >
                    <div className="modal-header text-white">
                        <h5 className="modal-title fs-4">{t("shared.sort_locations", "Sort Locations")}</h5>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            onClick={() => setSortModal(false)}
                            aria-label={t("shared.close", "Close")}
                        ></button>
                    </div>

                    <div className="modal-body p-4">
                        <div className="mb-3">
                            <label className="form-label fw-medium">{t("shared.sort_by", "Sort By")}</label>
                            <div className="btn-group w-100" role="group">
                                <input
                                    type="radio"
                                    className="btn-check"
                                    name="sortField"
                                    id="sortCreatedAt"
                                    autoComplete="off"
                                    checked={sortCriteria.field === 'createdAt'}
                                    onChange={() => setSortCriteria({...sortCriteria, field: 'createdAt'})}
                                />
                                <label className="btn btn-outline-primary" htmlFor="sortCreatedAt">
                                    {t("shared.created_date", "Created Date")}
                                </label>

                                <input
                                    type="radio"
                                    className="btn-check"
                                    name="sortField"
                                    id="sortUpdatedAt"
                                    autoComplete="off"
                                    checked={sortCriteria.field === 'updatedAt'}
                                    onChange={() => setSortCriteria({...sortCriteria, field: 'updatedAt'})}
                                />
                                <label className="btn btn-outline-primary" htmlFor="sortUpdatedAt">
                                    {t("shared.updated_date", "Updated Date")}
                                </label>

                                <input
                                    type="radio"
                                    className="btn-check"
                                    name="sortField"
                                    id="sortName"
                                    autoComplete="off"
                                    checked={sortCriteria.field === 'locationName'}
                                    onChange={() => setSortCriteria({...sortCriteria, field: 'locationName'})}
                                />
                                <label className="btn btn-outline-primary" htmlFor="sortName">
                                    {t("shared.name", "Name")}
                                </label>
                            </div>
                        </div>

                        <div className="mb-3">
                            <label className="form-label fw-medium">{t("shared.sort_direction", "Sort Direction")}</label>
                            <div className="btn-group w-100" role="group">
                                <input
                                    type="radio"
                                    className="btn-check"
                                    name="sortDirection"
                                    id="sortAsc"
                                    autoComplete="off"
                                    checked={sortCriteria.direction === 'asc'}
                                    onChange={() => setSortCriteria({...sortCriteria, direction: 'asc'})}
                                />
                                <label className="btn btn-outline-primary" htmlFor="sortAsc">
                                    {t("shared.ascending", "Ascending")}
                                </label>

                                <input
                                    type="radio"
                                    className="btn-check"
                                    name="sortDirection"
                                    id="sortDesc"
                                    autoComplete="off"
                                    checked={sortCriteria.direction === 'desc'}
                                    onChange={() => setSortCriteria({...sortCriteria, direction: 'desc'})}
                                />
                                <label className="btn btn-outline-primary" htmlFor="sortDesc">
                                    {t("shared.descending", "Descending")}
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="modal-footer bg-light border-top-0">
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => {
                                setSortCriteria({ field: 'createdAt', direction: 'desc' });
                                setSortModal(false);
                            }}
                        >
                            {t("shared.reset", "Reset")}
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => setSortModal(false)}
                        >
                            {t("shared.apply_sort", "Apply Sort")}
                        </button>
                    </div>
                </motion.div>
            </Modal>


            {/* Add/Edit Modal */}
            <Modal size="lg" isOpen={modal_large} toggle={() => tog_large()}>
                <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                >
                    <div className="modal-header">
                        <h5 className="modal-title" id="myLargeModalLabel">
                            {selectedEntry?.id ? t("shared.edit_location", 'Edit Location') : t("business.locations.add_location", 'Add Location')}
                        </h5>
                        <button onClick={handleModalClose} type="button" className="btn-close" aria-label={t("shared.close", "Close")}></button>
                    </div>
                    
                    <div className="modal-body">
                        <form onSubmit={handleUpdate}>
                            <Row className="mb-4">
                                <label htmlFor="locationName" className="col-sm-3 col-form-label">{t("shared.name", "Name")}</label>
                                <Col sm={9}>
                                    <input
                                        type="text"
                                        id="locationName"
                                        className={`form-control ${errors.locationName ? 'is-invalid' : ''}`}
                                        value={selectedEntry?.locationName || ''}
                                        onChange={(e) => setSelectedEntry({...selectedEntry, locationName: e.target.value})}
                                    />
                                    {errors.locationName && <small className="text-danger">{errors.locationName}</small>}
                                </Col>
                            </Row>
                             <Row className="mb-4">
                                <label htmlFor="locationAddress" className="col-sm-3 col-form-label">{t("shared.address", "Address")}</label>
                                <Col sm={9}>
                                    <input
                                        type="text"
                                        id="locationAddress"
                                        className={`form-control ${errors.locationAddress ? 'is-invalid' : ''}`}
                                        value={selectedEntry?.locationAddress || ''}
                                        onChange={(e) => setSelectedEntry({...selectedEntry, locationAddress: e.target.value})}
                                    />
                                    {errors.locationAddress && <small className="text-danger">{errors.locationAddress}</small>}
                                </Col>
                            </Row>

                            <Row className="mb-4">
                                <label className="col-sm-3 col-form-label">{t("shared.parent_location", "Parent Location")}</label>
                                <Col sm={9}>
                                    <ParentLocationSelector
                                        value={selectedEntry?.parentLocation || null}
                                        onChange={(loc) => setSelectedEntry({
                                            ...selectedEntry,
                                            parentLocation: loc,
                                            parentLocationId: loc?.id || null,
                                        })}
                                        excludeIds={selectedEntry?.id ? [selectedEntry.id] : []}
                                        placeholder={t("business.locations.search_parent_location", "Search for parent location...")}
                                    />
                                    <small className="text-muted">
                                        {t("business.locations.parent_location_hint", "Leave empty to keep this location at the root level.")}
                                    </small>
                                </Col>
                            </Row>

                            <div className="mt-4">
                                {apiError && <div className="alert alert-danger">{apiError}</div>}
                                <button type="submit" className="btn btn-success w-lg transition-all hover-grow pulse-on-hover">
                                    {selectedEntry?.id ? t("shared.update", 'Update') : t("shared.add", 'Add')}
                                </button>
                            </div>
                        </form>
                    </div>
                </motion.div>
            </Modal>
            
            {/* Delete Modal */}
            <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
                <div className="modal-header">
                    <h5 className="modal-title">{t("shared.confirm_delete", "Confirm Delete")}</h5>
                    <button type="button" className="btn-close" onClick={() => setDeleteModal(false)} aria-label={t("shared.close", "Close")}></button>
                </div>
                <div className="modal-body">
                    <p>{t("business.locations.delete_confirmation", "Are you sure you want to delete this Location? This action cannot be undone.")}</p>
                </div>
                <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setDeleteModal(false)}>
                        {t("shared.cancel", "Cancel")}
                    </button>
                    <button 
                        type="button" 
                        className="btn btn-danger" 
                        onClick={handleDelete}
                    >
                        {t("shared.delete", "Delete")}
                    </button>
                </div>
            </Modal>

            <FileModal
                title={t("business.locations.import_export_title", 'Import/Export Locations')}
                isOpen={fileModal}
                toggle={handleClose}
                data={locations}
                onImport={handleImport}
                onExport={handleExport}
                onDownloadTemplate={handleDownloadTemplate}
                error={apiError || locationImport.error}
                loading={loading}
                importLabel={t("business.locations.import_excel", "Import Locations from Excel")}
                exportLabel={t("business.locations.export_excel", "Export Locations to Excel")}
                uploadLabel={t("business.locations.upload_file", "Upload Locations File")}
                importJob={{
                    job: locationImport.job,
                    busy: locationImport.busy,
                    onConfirm: locationImport.commit,
                    onCancel: async () => { await locationImport.cancel(); setFileModal(false); },
                    onClose: () => { locationImport.reset(); setFileModal(false); },
                    fetchAllIssues: locationImport.fetchAllIssues,
                    columnLabels: importColumnLabels,
                }}
            />
            <UserModal
                isOpen={reportedByModal.isOpen}
                toggle={() => setReportedByModal(prev => ({ ...prev, isOpen: false }))}
                userData={reportedByModal.userData}
                loading={reportedByModal.loading}
                formatCreatedDate={formatCreatedDate}
                onTicketClick={handleTicketClick}
                fetchUserWorkOrders={handleReportedByClick}
            />
            <TicketDetailsModal
                isOpen={ticketModal.isOpen}
                enumData={enumData}
                toggle={() => setTicketModal(prev => ({ ...prev, isOpen: false }))}
                ticketNumber={ticketModal.ticketData?.ticketNumber}
                loading={ticketModal.loading}
                formatCreatedDate={formatCreatedDate}
                onStatusChange={onStatusChange}
            />
            <LocationModal
                isOpen={detailsModal.isOpen}
                toggle={() => setDetailsModal(prev => ({ ...prev, isOpen: false }))}
                title={detailsModal.title}
                mainData={detailsModal.mainData}
                relatedData={detailsModal.relatedData}
                loading={detailsModal.loading}
                formatCreatedDate={formatCreatedDate}
                onTicketClick={handleTicketClick}
                locationData={detailsModal.locationData}
                initialActiveTab={detailsModal.initialActiveTab}
                onEditLocation={(loc) => {
                    setDetailsModal(prev => ({ ...prev, isOpen: false }));
                    openEditWithParentFetch(loc);
                }}
                onCreateSubLocation={(parentLoc) => {
                    setSelectedEntry({
                        locationName: '',
                        locationAddress: '',
                        parentLocation: parentLoc,
                        parentLocationId: parentLoc?.id || null,
                    });
                    setErrors({});
                    setApiError('');
                    setmodal_large(true);
                }}
                onLocationUpdated={() => fetchLocations()}
            />
        </div>
    )
};

export default Location;